import { useCallback } from 'react';
import { bridge, getRangeForEvent } from 'shared/src/utils';
import { useQueryClient } from '@tanstack/react-query';
import { queryKey } from '../constant/queryKey';
import type { GoodsBasicResponse } from 'shared/src/api/goodsService/getGoodsBasic/types';
import { goodsService } from 'shared/src/api/goodsService';
import type { GoodsDetailResponse } from 'shared/src/api/goodsService/getGoodsDetail/types';
import { useGoodsSno } from './useGoodsSno';

const useLogGoodsAnalyticsEvent = () => {
  const queryClient = useQueryClient();
  const goodsSno = useGoodsSno();

  return useCallback<
    (params: {
      name: string;
      channel?: Array<
        | 'firebase'
        | 'amplitude'
        | 'braze'
        | 'ablylog'
        | 'facebook'
        | 'airbridge'
      >;
      analytics?: Record<string, unknown>;
    }) => Promise<boolean>
  >(
    async params => {
      const cachedGoodsBasic = queryClient.getQueryData<
        GoodsBasicResponse['goods']
      >(queryKey.goodsBasic(goodsSno));
      const cachedGoodsDetail = queryClient.getQueryData<GoodsDetailResponse>(
        queryKey.goodsDetail(goodsSno),
      );

      console.log('cachedGoodsDetail', cachedGoodsDetail);
      const goodsBasic =
        cachedGoodsBasic ??
        (await queryClient.fetchQuery({
          queryKey: queryKey.goodsBasic(goodsSno),
          queryFn: () => goodsService.getGoodsBasic(goodsSno),
        }));

      const goodsDetail =
        cachedGoodsDetail ??
        (await queryClient.fetchQuery({
          queryKey: queryKey.goodsDetail(goodsSno),
          queryFn: () => goodsService.getGoodsDetail(goodsSno),
        }));

      if (!goodsBasic) return false;

      if (!goodsDetail) return false;

      bridge.logAnalyticsEvent({
        name: params.name,
        channel: params.channel ?? [
          'firebase',
          'facebook',
          'amplitude',
          'braze',
          'ablylog',
        ],
        analytics: {
          SCREEN: 'GOODS_DETAIL',
          AGE_TAG1: goodsBasic.market.age_tags[0],
          AGE_TAG2: goodsBasic.market.age_tags[1],
          CATEGORY_DEPTH: goodsBasic.display_categories[0].depth,
          CATEGORY_NAME: goodsBasic.display_categories[0].name,
          CATEGORY_SNO: goodsBasic.display_categories[0].sno.toString(),
          DELIVERY_TYPE: goodsBasic.delivery_type_info.delivery_type,
          DISCOUNT_RATE: goodsBasic.price_info.discount_rate,
          FAVORITES_COUNT: goodsDetail.goods.market.score.favorites_count,
          FAVORITES_COUNT_RANGE: getRangeForEvent({
            target: goodsDetail.goods.market.score.favorites_count,
            rangeUnit: 500,
          }),
          GOODS_NAME: goodsBasic.name,
          GOODS_SNO: goodsBasic.sno.toString(),
          // "IS_AD": "0",
          IS_FAVORITED: goodsDetail.goods.market.is_favorite ? '1' : '0',
          IS_NEW: goodsBasic.is_new ? '1' : '0',
          LIKES_COUNT: goodsDetail.goods.score.likes_count,
          LIKES_COUNT_RANGE: getRangeForEvent({
            target: goodsDetail.goods.score.likes_count,
            rangeUnit: 500,
          }),
          MARKET_NAME: goodsBasic.market.name,
          MARKET_SNO: goodsBasic.market.sno.toString(),
          MARKET_TYPE: goodsBasic.market.market_vertical_type.name,
          ORIGINAL_PRICE: goodsBasic.price_info.consumer,
          REVIEW_COUNT: goodsDetail.goods.score.total_review_count,
          REVIEW_COUNT_RANGE: getRangeForEvent({
            target: goodsDetail.goods.score.total_review_count,
            rangeUnit: 500,
          }),
          REVIEW_RATING: goodsBasic.positive_review_rate,
          SALES_PRICE: goodsBasic.price_info.thumbnail_price,
          SALES_PRICE_RANGE: getRangeForEvent({
            target: goodsBasic.price_info.thumbnail_price,
            rangeUnit: 5000,
          }),
          STANDARD_CATEGORY_NAME: goodsBasic.standard_category.name,
          STANDARD_CATEGORY_SNO: goodsBasic.standard_category.sno.toString(),
          STYLE_TAG1: goodsBasic.market.style_tags[0],
          STYLE_TAG2: goodsBasic.market.style_tags[1],
          // "TYPE": "SIMPLE",
          ...params.analytics,
        },
      });

      return true;
    },
    [queryClient, goodsSno],
  );
};

export const useLogClickFavoriteMarket = () => {
  const logMarketAnalytics = useLogGoodsAnalyticsEvent();

  return useCallback<(isUnFavorite: boolean) => Promise<void>>(
    async isUnFavorite => {
      await logMarketAnalytics({
        name: isUnFavorite
          ? 'CLICK_UNFAVORITE_MARKET'
          : 'CLICK_FAVORITE_MARKET',
        analytics: {
          EVENT_TYPE: isUnFavorite
            ? 'CLICK_UNFAVORITE_MARKET'
            : 'CLICK_FAVORITE_MARKET',
        },
      });
    },
    [logMarketAnalytics],
  );
};
